import UtilityHelper from '../helpers';
import { staticConstants } from '../constants';
import BaseActionCreator from '../../actioncreators/BaseActionCreator';
import obligationSerializer from '../../actioncreators/obligations/serializer';
import tokenClient from 'ohcm-identity-csrf-token-client';
import axios from 'axios';

// =============================================================================
// axios 0.x → 1.x + nock 9.x → 13.x: Environment Validation Helper
// =============================================================================
// In axios 0.x, errors were handled synchronously before Jest cleanup.
// In axios 1.x, error handlers run asynchronously and can execute AFTER
// Jest has torn down JSDOM, when window.document becomes null.
// This helper checks if the environment is still valid before accessing globals.
// IMPACT: Test environment only - in production, window.document is never null.
// =============================================================================
const isEnvironmentValid = () => {
  try {
    return typeof window !== 'undefined' && window.document !== null;
  } catch (e) {
    /* istanbul ignore next */
    return false;
  }
};

// =============================================================================
// axios 0.x → 1.x: Headers Compatibility Helper
// =============================================================================
// In axios 0.x, config.headers was a plain JavaScript object.
// In axios 1.x, config.headers is an AxiosHeaders class instance.
// Spreading AxiosHeaders directly ({...config.headers}) doesn't work because
// AxiosHeaders uses internal symbols. Must convert via toJSON() before spreading.
// IMPACT: Production - required for axios 1.x to work correctly.
// =============================================================================
const toPlainHeaders = (headers) => {
  if (!headers) return {};
  if (typeof headers.toJSON === 'function') {
    return headers.toJSON();
  }
  /* istanbul ignore next */
  return { ...headers };
};

const supportedErrorCodes = {
  INCORRECT_CREDS: 'INCORRECT_CREDS',
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
  CAPTCHA_MISSING: 'RECAPTCHA_RESPONSE_TOKEN_MISSING',

  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  MISSING_SESSION_COOKIE: 'MISSING_SESSION_COOKIE',

  TOKEN_LOAD_VERIFY_FAILURE: 'TOKEN_LOAD_VERIFY_FAILURE',
  TOKEN_EXECUTE_FAILURE: 'TOKEN_EXECUTE_FAILURE',
  INVALID_TOKEN: 'INVALID_TOKEN',
  REDIRECT_TO_AIM_REGISTRATION_ERROR: 'REDIRECT_TO_AIM_REGISTRATION_ERROR',

  DUPLICATE_EMAIL: 'DUPLICATE_EMAIL',
  DUPLICATE_USERNAME: 'DUPLICATE_USERNAME',
  TOO_WEAK_PASSWORD: 'TOO_WEAK_PASSWORD',
  PASSWORD_BLACKLISTED: 'PASSWORD_BLACKLISTED',
  PASSWORD_3_SEQUENTIAL_CHARS: 'PASSWORD_3_SEQUENTIAL_CHARS',
  PASSWORD_3_REPEATED_CHARS: 'PASSWORD_3_REPEATED_CHARS',
  PASSWORD_PRECONDITION_UNMATCHED: 'PASSWORD_PRECONDITION_UNMATCHED',
  LENGTH_RULE: 'LENGTH_RULE',
  ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',

  INCORRECT_PASSWORD: 'INCORRECT_PASSWORD',

  FAILED_TO_GET_SETTINGS: 'FAILED_TO_GET_SETTINGS',
  BLOCK_BY_OBLIGATION: 'BLOCK_BY_OBLIGATION',
  BLOCKED_BY_OBLIGATION: 'BLOCKED_BY_OBLIGATION',

  EMAIL_ADDRESS_ALREADY_EXISTS: 'EMAIL_ADDRESS_ALREADY_EXISTS',
  EMAIL_IS_ALREADY_VERIFIED: 'EMAIL_IS_ALREADY_VERIFIED',

  ASSOCIATE_SUSPENDED: 'ASSOCIATE_SUSPENDED',

  ERROR_VERIFYING_ASSOCIATE: 'ERROR_VERIFYING_ASSOCIATE',

  CLIENT_NOT_FOUND: 'CLIENT_NOT_FOUND',
  CLIENT_MISSING_SAML_CONFIGURATION: 'CLIENT_MISSING_SAML_CONFIGURATION',
};

class HttpClient {
  static actionCreator = new BaseActionCreator();

  static createInstance(instanceConfig) {
    const { skipErrorHandling, ...axiosConfig } = instanceConfig;
    const axiosInstance = axios.create(axiosConfig);

    axiosInstance.interceptors.request.use((config) => {
      const reqHeader = {
        Accept: 'application/json',
        'x-trace-id': UtilityHelper.generateUniqueId(),
      };
      const csrfHeader = { 'X-Csrf-Token': tokenClient.fetchCsrfToken() || '' };

      // axios 0.x → 1.x: Convert AxiosHeaders to plain object before spreading
      // IMPACT: Production - required for headers to work correctly in axios 1.x
      const existingHeaders = toPlainHeaders(config.headers);

      if (config.method === 'get') {
        return {
          ...config,
          headers: {
            ...existingHeaders,
            ...reqHeader,
          },
        };
      }
      return {
        ...config,
        headers: {
          ...existingHeaders,
          ...reqHeader,
          ...csrfHeader,
        },
      };
    }, null);

    // =========================================================================
    // axios 0.x → 1.x: 204 Response Body Normalization
    // =========================================================================
    // In axios 0.x, 204 No Content responses returned data: '' (empty string).
    // In axios 1.x, 204 No Content responses return data: null.
    // Existing code checks `if (response.data)` which behaves differently for '' vs null.
    // This interceptor normalizes the behavior to match axios 0.x.
    // IMPACT: Production - ensures consistent behavior for 204 responses.
    // =========================================================================
    axiosInstance.interceptors.response.use((response) => {
      if (response.status === 204 && response.data === null) {
        response.data = '';
      }
      return response;
    }, null);

    if (!skipErrorHandling) {
      // eslint-disable-next-line no-use-before-define
      axiosInstance.interceptors.response.use(null, handleError);
    }
    return axiosInstance;
  }
}

const HANDLED_FORBIDDEN_CODES = {
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
};

const instantiateErrorWithCodeAndMsg = ({ code, message, errors }) => {
  const error = new Error(message);

  error.code = code;
  error.errors = errors;

  return error;
};

const getErrorCodeAndMsg = (errors) => ({
  code:
    (Array.isArray(errors) && errors[0] && supportedErrorCodes[errors[0].code]) || 'UNKNOWN_ERROR',
  message:
    (Array.isArray(errors) && errors[0] && errors[0].message) ||
    'Something went wrong while making a HTTP request.',
  errors,
});

const populateCurrentErrorStack = (error) => {
  // axios 0.x → 1.x + nock 9.x → 13.x: In axios 1.x, error handlers run async
  // after Jest tears down JSDOM, causing window.document to be null.
  // IMPACT: Test environment only - prevents crashes during test cleanup.
  if (!isEnvironmentValid()) return;

  if (Array.isArray(window.currentErrorStack)) {
    window.currentErrorStack.unshift(error);
  } else {
    window.currentErrorStack = [error];
  }
};

const throwIfForbiddenErrorIsHandled = (response) => {
  const errors = getErrorCodeAndMsg(response.data.errors);

  if (HANDLED_FORBIDDEN_CODES[errors.code]) {
    throw instantiateErrorWithCodeAndMsg(errors);
  }
};

const getRequiredAction = (obligation) => {
  if (obligation.requiredActions) {
    return obligation.requiredActions[0];
  }
  return obligation.requiredAction;
};

const reconstructUrl = (url) => {
  // axios 0.x → 1.x + nock 9.x → 13.x: In axios 1.x, error handlers run async
  // after Jest tears down JSDOM, causing window.location access to crash.
  // IMPACT: Test environment only - prevents "Cannot read properties of null" crashes.
  if (!isEnvironmentValid()) {
    /* istanbul ignore next */
    return url;
  }
  const split = url.split('?');
  const existingRedirect = split.length > 1 ? '&' : '?';
  const updatedURL = `${url}${existingRedirect}redirectUrl=${UtilityHelper.getCurrentPath()}`;

  return updatedURL;
};

const handleError = async (error) => {
  // ===========================================================================
  // axios 0.x → 1.x + nock 9.x → 13.x: Handle post-cleanup errors gracefully
  // ===========================================================================
  // In axios 0.x, errors were handled synchronously before Jest cleanup.
  // In axios 1.x, error interceptors run asynchronously and can execute AFTER
  // Jest has torn down JSDOM. These late errors crash Node.js process.
  // Return never-resolving promise to silently swallow post-cleanup errors.
  // IMPACT: Test environment only - in production, JSDOM is never torn down.
  // ===========================================================================
  /* istanbul ignore next */
  if (!isEnvironmentValid()) {
    return new Promise(() => {});
  }

  let errors;
  let requireSomeAction;

  populateCurrentErrorStack(error);

  if (error.response && error.response.data) {
    const responseData = error.response.data;

    requireSomeAction = responseData.obligation && getRequiredAction(responseData.obligation);

    if (error.response.status === staticConstants.statusCodes.FORBIDDEN) {
      throwIfForbiddenErrorIsHandled(error.response);
      let unauthorizedErrorPage = staticConstants.UNAUTHORIZED_ERROR_PAGE;
      const errResponse = responseData.errors && responseData.errors[0];

      if (errResponse && errResponse.details && errResponse.details.traceId) {
        unauthorizedErrorPage = unauthorizedErrorPage.concat(
          `?traceId=${encodeURIComponent(errResponse.details.traceId)}`
        );
      }
      UtilityHelper.redirectTo(unauthorizedErrorPage);
      return null;
    }

    if (requireSomeAction) {
      const { kind, url } = requireSomeAction;

      if (kind === 'redirect') {
        if (UtilityHelper.requiresRedirect(url)) {
          UtilityHelper.redirectTo(reconstructUrl(url));
        }
        return null;
      }
      if (kind === staticConstants.obligationActionTypes.COMMAND) {
        const action = obligationSerializer.fromActionRequiredResponse(requireSomeAction);

        HttpClient.actionCreator.handleObligations(action);
      }
    }

    errors = responseData.errors || [error];
  } else {
    errors = [error];
  }
  throw instantiateErrorWithCodeAndMsg(getErrorCodeAndMsg(errors));
};

export { HttpClient as default, supportedErrorCodes };
